import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Crown, Sparkles, X } from "lucide-react";

/**
 * Soft paywall banner: shows current billing status and pushes upgrade CTA.
 * - Hidden if user is on premium (subscription or whitelist).
 * - Yellow/warning style when free quota exhausted (hard limit).
 * - Blue/info style when quota still available (soft nudge).
 * - Dismissable per-session via sessionStorage to avoid being annoying.
 */
export default function BillingBanner({ variant = "default" }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("billing-banner-dismissed") === "1");

  useEffect(() => {
    api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  if (!status || dismissed) return null;
  // Hide if user has any paid plan (base/premium via stripe or whitelist)
  if (status.plan === "premium" || status.plan === "base") return null;
  if (status.source === "whitelist") return null;

  const quota = status.free_quota || 0;
  const used = status.events_used || 0;
  const exhausted = used >= quota;

  const dismiss = () => {
    sessionStorage.setItem("billing-banner-dismissed", "1");
    setDismissed(true);
  };

  if (exhausted) {
    return (
      <div data-testid="billing-banner-exhausted" className="relative rounded-xl p-5 md:p-6 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border-2 border-amber-300 shadow-sm">
        <button onClick={dismiss} className="absolute top-3 right-3 text-amber-700/60 hover:text-amber-900" aria-label="chiudi">
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between pr-6">
          <div className="flex gap-3 items-start">
            <div className="w-10 h-10 rounded-full bg-amber-200 grid place-items-center shrink-0">
              <Crown className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="font-display text-xl text-amber-900">Hai usato il tuo evento gratuito!</p>
              <p className="text-sm text-amber-800 mt-1">
                Sblocca <strong>eventi illimitati</strong>, preventivi AI, OCR fatture e calendario visivo a partire da <strong>€9,90/mese</strong>.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/piani")}
            data-testid="btn-upgrade-now"
            className="btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500 whitespace-nowrap"
          >
            <Crown className="w-4 h-4" /> Sblocca ora
          </button>
        </div>
      </div>
    );
  }

  // Soft variant — still has quota left
  return (
    <div data-testid="billing-banner-soft" className="relative rounded-xl p-4 bg-[#EFF6FF] border border-[#2563EB]/30">
      <button onClick={dismiss} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" aria-label="chiudi">
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between pr-6">
        <div className="flex gap-2.5 items-center">
          <Sparkles className="w-4 h-4 text-[#2563EB]" />
          <p className="text-sm text-[#0F2741]">
            Trial gratuito attivo · <strong>{used}/{quota}</strong> {quota === 1 ? "evento usato" : "eventi usati"}. Passa a un piano per eventi illimitati.
          </p>
        </div>
        <button
          onClick={() => navigate("/piani")}
          data-testid="btn-view-plans"
          className="text-xs font-semibold text-[#1D4ED8] hover:text-[#1E3A8A] underline whitespace-nowrap"
        >
          Vedi piani →
        </button>
      </div>
    </div>
  );
}
